/**
 * Zod schema for the `invitations` collection.
 *
 * Thin invitation record — links an email to an event. Identity/profile
 * data lives on `contacts`, not here (ADR-057).
 *
 * An invitation represents the act of an organizer adding someone to an
 * event. Status semantics (per ADR-089 cleanup):
 *
 *   - `sent` — organizer added this person to this event. If the target
 *     was already an authenticated user, a membership was created
 *     alongside this invitation. If the target was a contact, the
 *     membership is created when the contact authenticates (via
 *     activateAccount).
 *   - `revoked` — organizer removed this person from this event. If a
 *     membership existed, it was deleted along with all event-scoped
 *     scoped data (cascade). The invitation itself remains as audit.
 *
 * No "claimed" state — the only claim event in the system is the contact
 * → user transition (a property of the identity, not the invitation).
 * Account Status (Active vs Pending) on the roster UI is derived at
 * render time from whether the email maps to a `users/{uid}` doc or only
 * a `contacts/{id}` doc — it is not stored on the invitation.
 *
 * All writes via Cloud Functions (Admin SDK) — direct client writes are
 * blocked at the rules layer.
 */

import { z } from 'zod';
import type { TimestampLike } from '../types/common';

export const invitationStatusSchema = z.enum(['sent', 'revoked']);
/**
 * Delivery state of the most recent system send.
 *
 *   - `queued`     — mail doc written, waiting on the Trigger Email extension
 *   - `sent`       — extension reported SMTP acceptance
 *   - `failed`     — extension reported an SMTP error (see emailDeliveryError)
 *   - `suppressed` — SUPPRESS_EMAILS was on; no mail doc was written
 */
export const invitationEmailDeliveryStatusSchema = z.enum([
  'queued',
  'sent',
  'failed',
  'suppressed',
]);

export const invitationDocumentSchema = z.object({
  eventId: z.string(),
  /**
   * Email the organizer entered when creating the invitation. Used for the
   * first lookup (email → person) and as the address the email goes to.
   * NOT an identity key: `personId` is.
   */
  email: z.string(),

  /**
   * The person this invitation was issued for (ADR-193). Set by
   * `issueInvitationsForExistingPersons` on every new invitation; absent on
   * rows written before 2026-09-08, which readers must fall back to matching
   * by email. Whether someone is on an event is answered by their
   * `eventParticipants` row, never by this record.
   */
  personId: z.string().nullable().optional(),

  /**
   * Stable one-person audience for this invitee in this event. For pending
   * contacts this is created before authentication and has no resolved user
   * until activateAccount claims the contact. For active users it points at
   * their existing/bootstrapped personal audience. Optional for legacy
   * invitation rows written before the field existed.
   */
  personalAudienceId: z.string().nullable().optional(),

  /**
   * 8-char alphanumeric code for app-level identity proof. Only populated
   * for contact→event invitations (recipient hasn't authenticated yet —
   * code is used to validate first-time sign-in identity match).
   */
  code: z.string().nullable(),

  status: invitationStatusSchema,

  /** UID of the organizer/admin who revoked this invitation. */
  revokedByUid: z.string().nullable(),
  revokedAt: z.custom<TimestampLike>().nullable(),

  /** UID of the admin/organizer who created this invitation. */
  createdByUserId: z.string(),

  /** When the most recent invitation email was sent. */
  emailSentAt: z.custom<TimestampLike>().nullable(),

  /** Total number of invitation emails sent (initial + resends). */
  emailSendCount: z.number(),

  /** Delivery status of the most recent email. */
  emailDeliveryStatus: invitationEmailDeliveryStatusSchema.nullable(),
  emailDeliveryError: z.string().nullable(),

  /**
   * When an organizer recorded that they sent the invitation email out-of-band
   * (e.g. through their corporate mail system). Set by the
   * `markInvitationEmailSentManually` callable. Independent of `emailSentAt`,
   * which records system-driven sends — both can coexist.
   */
  emailMarkedSentManuallyAt: z.custom<TimestampLike>().nullable(),
  /** UID of the organizer who marked the email as sent manually. */
  emailMarkedSentManuallyByUid: z.string().nullable(),
});
